// src/domains/broadcast.lib.ts —— 推广推送纯函数（无状态、可单测）：Telegram HTML 白名单预览解析、字数计数、按钮链接预检、平台时区换算
import { fmtDateTime } from '../../../shared/src/time';
import type { Lang } from '../../../shared/src/dc';

// 强制退订尾注（拍板决定 5；后端逐字附加）。计数口径：可见文本 + 分隔空行 + 尾注（UTF-16 码元，同 Telegram 计数）
export const FOOTER: Readonly<Record<Lang, string>> = { zh: '回复 /stop_ads 退订推广消息', en: 'Reply /stop_ads to unsubscribe' };
export const FOOTER_SEP = '\n\n';
export const LIMIT_CAPTION = 1024; // 带图（caption）
export const LIMIT_TEXT = 4096; // 无图（message）
export const IMG_MAX_BYTES = 5 * 1024 * 1024;
export const IMG_TYPES: readonly string[] = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_BUTTONS = 3;
export const MAX_BOTS = 10;
export const TITLE_MAX = 64;

// ---- 白名单富文本预览 ----

// 只认 <b><i><u><s><code>（无属性）与 <a href="https://…">；其余一切（含 <script>、带属性的 <b onclick>、非 https 链接）按原文显示。
// 预览以「文本段」下发、模板按纯文本渲染：任何输入都不会被当作 HTML 解析（不经 innerHTML，杜绝 XSS）
export interface RichSeg {
  text: string;
  b: boolean;
  i: boolean;
  u: boolean;
  s: boolean;
  code: boolean;
  href: string | null;
}

type Fmt = 'b' | 'i' | 'u' | 's' | 'code' | 'a';
interface Open {
  tag: Fmt;
  href: string | null;
}

type SimpleFmt = Exclude<Fmt, 'a'>;
function isSimple(tag: string): tag is SimpleFmt {
  return tag === 'b' || tag === 'i' || tag === 'u' || tag === 's' || tag === 'code';
}
const TAG_RE = /<(\/?)([A-Za-z]+)((?:\s[^<>]*)?)>/g;
const HREF_RE = /^\s+href\s*=\s*(?:"([^"]*)"|'([^']*)')\s*$/i;
const ENTITY_RE = /&(lt|gt|amp|quot|#\d{1,7}|#[xX][0-9a-fA-F]{1,6});/g;

function decodeEntities(s: string): string {
  return s.replace(ENTITY_RE, (m, name: string) => {
    if (name === 'lt') return '<';
    if (name === 'gt') return '>';
    if (name === 'amp') return '&';
    if (name === 'quot') return '"';
    const cp = name[1] === 'x' || name[1] === 'X' ? Number.parseInt(name.slice(2), 16) : Number.parseInt(name.slice(1), 10);
    return Number.isInteger(cp) && cp > 0 && cp <= 0x10ffff && !(cp >= 0xd800 && cp <= 0xdfff) ? String.fromCodePoint(cp) : m;
  });
}

// 链接 href：仅 https、可解析、无 userinfo（与后端白名单重建口径一致）
export function safeHref(raw: string): string | null {
  const v = decodeEntities(raw.trim());
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' || u.username !== '' || u.password !== '' || u.hostname === '') return null;
  return u.href;
}

function openTag(name: string, attrs: string): Open | null {
  const tag = name.toLowerCase();
  if (isSimple(tag)) return attrs.trim() === '' ? { tag, href: null } : null;
  if (tag !== 'a') return null;
  const m = HREF_RE.exec(attrs);
  const href = m ? safeHref(m[1] ?? m[2] ?? '') : null;
  return href ? { tag: 'a', href } : null;
}

export function parseRich(text: string): RichSeg[] {
  const out: RichSeg[] = [];
  const stack: Open[] = [];
  const push = (raw: string, decode: boolean): void => {
    const t = decode ? decodeEntities(raw) : raw;
    if (!t) return;
    const has = (f: Fmt): boolean => stack.some((o) => o.tag === f);
    const link = [...stack].reverse().find((o) => o.tag === 'a');
    const seg: RichSeg = { text: t, b: has('b'), i: has('i'), u: has('u'), s: has('s'), code: has('code'), href: link?.href ?? null };
    const last = out[out.length - 1];
    if (last && last.b === seg.b && last.i === seg.i && last.u === seg.u && last.s === seg.s && last.code === seg.code && last.href === seg.href) last.text += t;
    else out.push(seg);
  };
  let pos = 0;
  for (const m of text.matchAll(TAG_RE)) {
    const at = m.index ?? 0;
    push(text.slice(pos, at), true);
    pos = at + m[0].length;
    const closing = m[1] === '/';
    const name = (m[2] ?? '').toLowerCase();
    if (closing) {
      const top = stack[stack.length - 1];
      if (top && top.tag === name && (m[3] ?? '').trim() === '') stack.pop();
      else push(m[0], false); // 未配对的结束标签：原文显示
      continue;
    }
    const o = openTag(m[2] ?? '', m[3] ?? '');
    if (o) stack.push(o);
    else push(m[0], false); // 非白名单 / 带属性 / 非法链接：原文显示
  }
  push(text.slice(pos), true);
  return out;
}

export function visibleText(text: string): string {
  return parseRich(text)
    .map((s) => s.text)
    .join('');
}

// 计入尾注后的长度（JS string.length 即 UTF-16 码元数，与 Telegram 计数一致）
export function countWithFooter(text: string, lang: Lang): number {
  return visibleText(text).length + FOOTER_SEP.length + FOOTER[lang].length;
}

export function textLimit(hasImage: boolean): number {
  return hasImage ? LIMIT_CAPTION : LIMIT_TEXT;
}

export function segStyle(s: RichSeg): string {
  const deco = [s.u || s.href ? 'underline' : '', s.s ? 'line-through' : ''].filter(Boolean).join(' ');
  return [
    s.b ? 'font-weight:700' : '',
    s.i ? 'font-style:italic' : '',
    deco ? `text-decoration:${deco}` : '',
    s.code ? "font-family:'JetBrains Mono',monospace;background:#eef3f0;border-radius:4px;padding:0 3px" : '',
    s.href ? 'color:#1f6fa8' : '',
  ]
    .filter(Boolean)
    .join(';');
}

// ---- 按钮链接预检（拍板决定 6；服务端同口径复核） ----

export type UrlCheck = 'ok' | 'format' | 'scheme' | 'userinfo' | 'tme';

const TG_HOSTS: ReadonlySet<string> = new Set(['t.me', 'telegram.me']);
const TG_USER_PATH = /^\/[A-Za-z][A-Za-z0-9_]{3,31}\/?$/;
const TG_START_QUERY = /^\?start=[A-Za-z0-9_-]{1,64}$/;

export function checkButtonUrl(raw: string): UrlCheck {
  const v = raw.trim();
  if (!v) return 'format';
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return 'format';
  }
  if (u.protocol !== 'https:') return 'scheme';
  if (u.username !== '' || u.password !== '') return 'userinfo';
  if (u.hostname === '') return 'format';
  if (TG_HOSTS.has(u.hostname.toLowerCase())) {
    // 只允许公开 /<username>（可带 ?start=）；拒绝 /+xxx 私有邀请、/joinchat/…、频道帖子路径与其他参数
    if (!TG_USER_PATH.test(u.pathname) || u.hash !== '' || (u.search !== '' && !TG_START_QUERY.test(u.search))) return 'tme';
  }
  return 'ok';
}

// ---- 平台时区换算（once 的 run_at 为 UTC；表单按平台时区的墙上时间录入） ----

const WALL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const SHOWN_RE = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;
export const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
export const DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

// ts 时刻在 tz 的 UTC 偏移（毫秒，墙上时间 − UTC）；复用 shared/time 的 Intl 渲染（非法时区已在其内退回本地并告警）
function offsetAt(ts: number, tz: string): number {
  const m = SHOWN_RE.exec(fmtDateTime(new Date(ts).toISOString(), { tz, seconds: true }));
  if (!m) return 0;
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6]));
  return wall - Math.floor(ts / 1000) * 1000;
}

// 'YYYY-MM-DDTHH:MM'（tz 墙上时间）→ RFC3339 UTC；非法输入 null。DST 交界按第二次偏移修正
export function wallToUtc(wall: string, tz: string): string | null {
  const m = WALL_RE.exec(wall);
  if (!m) return null;
  const guess = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  if (Number.isNaN(guess)) return null;
  const o1 = offsetAt(guess, tz);
  let ts = guess - o1;
  const o2 = offsetAt(ts, tz);
  if (o2 !== o1) ts = guess - o2;
  return new Date(ts).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// RFC3339 UTC → 'YYYY-MM-DDTHH:MM'（tz 墙上时间，datetime-local 输入框值）；非法输入 ''
export function utcToWall(iso: string | null | undefined, tz: string): string {
  const s = fmtDateTime(iso, { tz });
  return s === '—' ? '' : s.replace(' ', 'T');
}
